import { BaseCurrency, isDefined, LoadableState, NbSelectItemOption } from '@eih-netbroker/common';
import { useLocale } from '../../../../context/NbLocalizationContext';
import { MoneyMaskHOC } from '../../../../shared/components/money-mask/MoneyMask';
import { NbSelect } from '../../../../shared/components/select/Select';
import { NbTextField } from '../../../../shared/components/text-field/TextField';
import { useFormatter } from '../../../../shared/services/utils/useFormatter';
import classes from './CashActionAccountSelector.module.scss';

type CashActionAccountSelectorProps<T> = {
  isSource?: boolean;
  showErrorBorder?: boolean;
  setAmountToMaxLabel?: string;
  selectedAccount?: T | string;
  accountCurrency: string;
  accountOptions?: LoadableState<Array<NbSelectItemOption<T>>>;
  availableAmount?: number;
  disabled?: boolean;
  handleAccountChange?: (value: T | string) => void;
  handleAmountChange?: (value: string) => void;
};

export const CashActionAccountSelector = <T,>({
  isSource,
  showErrorBorder,
  selectedAccount,
  accountCurrency,
  accountOptions,
  availableAmount,
  setAmountToMaxLabel,
  disabled,
  handleAccountChange,
  handleAmountChange,
}: CashActionAccountSelectorProps<T>) => {
  const { translation } = useLocale();
  const { formatNumberRoundOrF2 } = useFormatter();

  const formatBalance = (value?: string | number, currency?: string) => {
    if (!isDefined(value)) {
      return '-';
    }

    return `${formatNumberRoundOrF2(value)} ${currency ?? BaseCurrency}`;
  };

  return (
    <div className={classes.transferColumn}>
      <div className={classes.selectorBlock}>
        <p className={classes.selectorLabel}>
          {translation.cashAction.deposit.internal[isSource ? 'sourceAccountLabel' : 'targetAccountLabel']}
        </p>
        {isDefined(accountOptions) && isDefined(handleAccountChange) ? (
          <NbSelect<T | string>
            value={selectedAccount ?? ''}
            options={accountOptions.isLoaded ? accountOptions : []}
            onChange={event => handleAccountChange(event.target.value)}
            size="small"
            isLoading={accountOptions.isLoading}
            className={classes.accountSelect}
            error={showErrorBorder}
            enforceErrorBordersWithoutMessage
            disabled={disabled}
          />
        ) : (
          <NbTextField
            value={selectedAccount}
            className={classes.accountField}
            size="small"
            readOnly
            error={showErrorBorder}
          />
        )}
      </div>

      {isDefined(availableAmount) && (
        <div className={classes.balanceColumn}>
          <div className={classes.balanceSection}>
            <p className={classes.balanceText}>{translation.cashAction.deposit.internal.balanceLabel}</p>
            <MoneyMaskHOC
              isInline
              size={100}
              isInverse
            >
              <p className={classes.balanceText}>{formatBalance(availableAmount, accountCurrency)}</p>
            </MoneyMaskHOC>
          </div>
          {isSource && isDefined(handleAmountChange) && !showErrorBorder && (
            <button
              type="button"
              className={classes.totalDeposits}
              onClick={() => handleAmountChange(availableAmount.toString())}
            >
              {setAmountToMaxLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );
};
